import { HttpClient } from '@angular/common/http';
import { ElementRef, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import Snackbar from 'awesome-snackbar';
import { LIBRARY_VERSION } from 'src/libs/SipJS/version';
import Swal from 'sweetalert2';
import { DialogAccessibilityComponent } from '../components/dialog-accessibility/dialog-accessibility.component';
import { MultimediaDialogComponent } from '../components/multimedia-manager/multimedia-dialog.component';
import { MenuCreditsComponent } from '../components/partecipants/menu/credits.component';
import { AppHelper } from '../helpers/app.helper';
import { AppState } from '../store/states/app.state';
import { AppConfigService } from './app.config.service';
import { I18Service } from './i18.service';
import { MenuCloseService } from './menu-close.service';

export type LicItem = {
  name: string,
  copyright: string,
  publisher: string,
  licenses: string,
  version: string
}

export type PopupData = {msg: string, title?: string, confirmButton?: string, cancelButton?: string, preConfirm?: any, blocking?: boolean}

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private static s_snackBar: MatSnackBar;
    private static s_config: AppConfigService;
    private static s_matDialog: MatDialog;
    public static s_dialogRef: MatDialogRef<any>;
    private static s_accessibilityRef: MatDialogRef<any>;
    private static s_menuClose: MenuCloseService;

    private static messageQueue: { message: string, duration: number }[] = []
    public static popupDownload?: ElementRef;

    // private static defaultTime = 2000;
    constructor(snackBar: MatSnackBar, config: AppConfigService, http: HttpClient, matDialog: MatDialog, menuClose: MenuCloseService) {
        console.debug(`NotificationService constructor`)
        NotificationService.s_snackBar = snackBar;
        NotificationService.s_config = config;
        NotificationService.s_matDialog = matDialog;
        NotificationService.s_menuClose = menuClose;
        this.getLicenses(http);
    }
    static show(message: string, snackDuration = 0) {
        this.addMessage(message, snackDuration);
    }

    static showSuccess(message: string, snackDuration: number = 0): void {
        this.addMessage(`${this.icon('tick')}${this.icon('space')}${message}`, snackDuration);
    }

    static showError(message: string, snackDuration: number = 0): void {
        this.addMessage(`${this.icon('warn')}${this.icon('space')}${message}`, snackDuration);
    }

    private static addMessage(message: string, duration: number): void {
        if (!duration) duration = this.getDefaultDuration();
        
        if (this.messageQueue.some( z => z.message == message)) return; //evitare duplicati
        this.messageQueue.push({ message, duration });
        console.log(`****** snackbar message: ${message}`)
        new Snackbar(message, {
            position: 'top-center',
            timeout: duration, //+ 60000000,
            actionText: this.icon('close'),
            waitForEvent: false, //non attende che ci sia la "pagina attiva"
            style: {
                message: [['white-space', 'pre'], ['overflow', 'hidden'], ['text-overflow', 'ellipsis'],['max-width', 'min(80dvw,80vw)']]
            },
            afterHide: () => { 
                this.messageQueue.forEach( (item, idx) => {
                    if (item.message == message) this.messageQueue.splice(idx,1);
                })
            }
        });



        

        // // if (!this.processingMessage) {
        //     this.displaySnackbar();
        // // }
    }
    private static getNextMessage() {
        return this.messageQueue.length ? this.messageQueue.shift() : undefined;
    }

    private static displaySnackbar() {
        // const next = this.getNextMessage();

        // if (!next) {
        //     this.processingMessage = false; // No message in the queue, set flag false
        //     return;
        // }

        // this.processingMessage = true; // A message was dequeued and is being processed

        // const duration = (this.messageQueue.length > 1) ? next.duration/2 : next.duration;
        // const message = next.message
        if (!this.messageQueue.length) return;
        const message = this.messageQueue.map( z => z.message.trim()).join('\n')
        const duration = this.messageQueue[0].duration;

        NotificationService.s_snackBar.open(message, '', {
            duration,
            verticalPosition: 'top',
            horizontalPosition: 'center',
            panelClass: ['snackbar-multiline']
        }).afterDismissed()
            .subscribe(() => {
                // this.displaySnackbar();
                this.messageQueue = [];
            });;
    }

    private static getDefaultDuration(): number {
        // return 100000;
        return (NotificationService.s_config.agencySettings?.snackbarDefaultTimer)
            ? NotificationService.s_config.agencySettings!.snackbarDefaultTimer : 2000;
    }


    //---
    public static popupInfo(data: PopupData): Promise<any> {
      const x = {...data, ...{iconHtml : '<span class="material-symbols-outlined azure">info</span>'}};
      return this.swalShow(x);
    }
    public static popupQuestion(data: PopupData): Promise<any> {
      const x = {...data, ...{iconHtml : '<span class="material-symbols-outlined azure">help</span>', cancelButton: data.cancelButton?? I18Service.get('POPUP.CANCEL')}};
      return this.swalShow(x);
    }
    public static popupSuccess(data: PopupData): Promise<any> {
      const x = {...data, ...{iconHtml : '<span class="material-symbols-outlined green">task_alt</span>'}};
      return this.swalShow(x);
    }
    public static popupWarn(data: PopupData): Promise<any> {
      const x = {...data, ...{iconHtml : '<span class="material-symbols-outlined orange">error</span>'}};
      return this.swalShow(x);
    }
    public static popupError(data: PopupData): Promise<any> {
      const x = {...data, ...{iconHtml : '<span class="material-symbols-outlined red">cancel</span>'}};
      return this.swalShow(x);
    }

    public static popupTimerQuestion(data: {msg:string, title?:string, timer?: number, cancelButton?:string})
    {
      data.timer = data.timer ?? 7000;
      data.cancelButton = data.cancelButton ?? I18Service.get('POPUP.CANCEL')
      const x = {...data, ...{iconHtml : '<span class="material-symbols-outlined orange">exclamation</span>'}};
      return this.swalShow(x);
    }

    public static popupClose() {
      Swal.close();
      this.s_matDialog.closeAll();
    }

    public static isOpen() {
      return Swal.isVisible() || this.s_matDialog.openDialogs.length > 0;
    }

    private static swalShow(data: any ): Promise<any> {
      
      const showCancelButton = data.cancelButton !== undefined 
      const blocking = showCancelButton || data.blocking;
      const confirmButton = data.confirmButton ?? 'OK';
      
      return Swal.fire({
            // title: this.selectorPlaceHolder.get('SA_TIMEOUTGLTITLE'),
          html: data.msg,
          confirmButtonText: confirmButton,
          cancelButtonText: data.cancelButton,
          timer: data.timer,
          timerProgressBar: !!data.timer,
          showCancelButton,
          allowOutsideClick: !blocking,
          allowEscapeKey: !blocking,
          iconHtml: data.iconHtml,
          preConfirm: data.preConfirm,
          showClass: {icon: 'swal2-icon-show'},
        })
    }

    public static timedToastWarn(msg: string, 
        confirmButton: string | undefined = undefined, 
        timer: number = 2000, 
        position: 'top' | 'center' | 'bottom' = 'top',
        width: string | undefined = undefined
        ): Promise<any> {
        return Swal.fire({
            html: msg,
            timer: timer,
            toast: true,
            confirmButtonText: confirmButton,
            // allowOutsideClick: confirmButton != undefined,
            showConfirmButton: confirmButton != undefined,
            timerProgressBar: true,
            position: position,
            width
        })
    }

    public static showDocumentToDownload(url: string, filename: string, who: string){
        Swal.fire({
            html: NotificationService.popupDownload,
            allowOutsideClick: false,
            showConfirmButton: false,
            showCloseButton: true,
            iconHtml: '<span class="material-symbols-outlined azure">info</span>',
            timer: 15000,
            showClass: {icon: 'swal2-icon-show'},
          });
        const swalPopup = document.getElementById('swal-document-popup');
        if (swalPopup) {
          swalPopup.onclick = () => {
              setTimeout(() => Swal.close(), 1000);
          };
      }
    }

    public static showCredits(): MatDialogRef<any> {
      NotificationService.s_dialogRef = NotificationService.s_matDialog.open(MenuCreditsComponent, {
        // width: '80%',
//        height: '55%',
     //   data: data,
        disableClose: false,
        panelClass: 'dialog-background'
      });
      return NotificationService.s_dialogRef;
    }

  // public static showSettings(): MatDialogRef<any> {
  //   NotificationService.s_dialogRef = NotificationService.s_matDialog.open(MenuSettingsComponent, {
  //     disableClose: true,
  //     panelClass: 'dialog-background'
  //   });
  //   return NotificationService.s_dialogRef;
  // }

    public static showMultimediaDialog(data: AppState): MatDialogRef<any> {
      NotificationService.s_dialogRef = NotificationService.s_matDialog.open(MultimediaDialogComponent, {
        width: '80%',
        height: '55%',
        minHeight: '300px',
        
        data: data,
        disableClose: false,
        panelClass: 'dialog-background'
      });
      return NotificationService.s_dialogRef;
    }

    public static showAccessibilityDialog(): MatDialogRef<any>{
      NotificationService.s_accessibilityRef = NotificationService.s_matDialog.open(DialogAccessibilityComponent, {
        data: {},
        width: '80%',
        panelClass: 'dialog-background'
      });
      return NotificationService.s_accessibilityRef;
    }

    public static closeMultimediaDialog() {
      NotificationService.s_dialogRef && NotificationService.s_dialogRef.close();
      NotificationService.s_accessibilityRef && NotificationService.s_accessibilityRef.close();
      
      NotificationService.s_menuClose.closeAllMenus();
      NotificationService.s_menuClose.dismissActiveBottomSheet();
    }

    public static showElement(html : ElementRef, titleText: string): Promise<any>{
        return Swal.fire({
          titleText,
          html,
          padding:'5px',
          // focusConfirm: false

          // focusConfirm: true,
          // backdrop: false,
          // allowOutsideClick: true,
          // allowEscapeKey: true,
          didOpen: ( x: HTMLElement ) => { x.focus(); }  // sposta il focus sul div in popup
        })
    }

    public static licenseList:LicItem[];
    private getLicenses(http: HttpClient) {
      const lic = 'assets/licenses.json?v=' + AppHelper.getBuild();
      http.get<LicItem[]>(lic, {responseType: 'json'}).subscribe( (response: LicItem[]) => {
         const licenses: LicItem[] = [];
         let angular: LicItem | undefined = undefined;
         let ngrx: LicItem | undefined = undefined;
         let ngxTranslate: LicItem | undefined = undefined;
         Object.keys(response).forEach( (module: any) => {
            const item = response[module]
            if(module.startsWith('@angular')){ // show generic angular for all submodules
              if( !angular && item.copyright && item.version) {
                angular = this.copyLicItem(item, {name: '@angular'});
                licenses.push(angular);
              }
            } else if (module.startsWith('@ngrx')){// show generic ngrx for all submodules
              if (!ngrx) {
                ngrx = this.copyLicItem(item, {name: '@ngrx', copyright: 'Powered by the Community ©2015-2019'});
                licenses.push(ngrx);
              }
            } else if (module.startsWith('@ngx-translate')){// show generic ngx-translate for all submodules
              if (!ngxTranslate) {
                ngxTranslate = this.copyLicItem(item);
                licenses.push(ngxTranslate);
              }
            }
            else if (module.startsWith('@material') || module.startsWith('@types')) {} // none
            else {
                var idx = licenses.findIndex( z => z.name == item.name);
                if (idx < 0){
                  licenses.push(this.copyLicItem(item));
                } else {
                  licenses.splice(idx, 1, item);
                }
            }
        });

        licenses.push({name: 'SIP.js', copyright: 'Copyright (c) 2020 Junction Networks', licenses: 'MIT', publisher:'', version: LIBRARY_VERSION});
        // console.log("licenze************", licenses);

        NotificationService.licenseList = licenses;

      });


  }
  private copyLicItem(s: LicItem, option: any = {}): LicItem
  {
    const item: LicItem = {
      name: (option.name) ?? s.name,
      copyright: (option.copyright) ? option.copyright : (s.copyright) ? s.copyright : `Copyright (c) ${s.publisher}`,
      licenses: s.licenses,
      publisher: s.publisher,
      version: s.version 
    }
    item.copyright = item.copyright.replace('&gt','>').replace('&lt','<').replace('>;','>').replace('<;','<').replace('\\>','>').replace('\\<','<')
    return item;
  }


  private static icon(icon: string, count = 1): string {
      let char = ''
      switch (icon) {
        // case 'phone':
        //   char = String.fromCodePoint(0x1F4DE);
        //   break;
        // case 'microphone':
        //   char = String.fromCodePoint(0x1F3A4);
        //   break;
        // case 'camera':
        //   char = String.fromCodePoint(0x1F3A5);
        //   break;
        // case 'disconnectss':
        //   char = String.fromCodePoint(0x26D4); // divieto accesso rosso
        //   break;
        case 'close':        
          char = String.fromCodePoint(0x2716) + String.fromCodePoint(0xFE0E);; // Heavy multiplication X
          break;
        // case 'disconnectZZ':
        //   char = String.fromCodePoint(0x274C) // X rossa
        //   break;
        // case 'connect':
        //   char = String.fromCodePoint(0x2705)+String.fromCodePoint(0xFE0E); // spunta verde
        //   break;
        // case 'position':
        //   char = String.fromCodePoint(0x1F30D);
        //   break
        case 'space':
          char = String.fromCodePoint(0x2003) + String.fromCodePoint(0xFE0E);;
          break;
        case 'warn':
          // char = String.fromCodePoint(0x2757); // punto esclamativo rosso
          // char = String.fromCodePoint(0x229d); // punto esclamativo rosso
          // char = `<span class="snackbar-icon red">&#xe002;</span>`;
          char = `<span class="material-symbols-outlined snackbar-icon red">warning</span>`;
          break;
        case 'tick': 
          // char = `<span class="snackbar-icon green">&#xe2e6;</span>`;
          char = `<span class="material-symbols-outlined snackbar-icon green">task_alt</span>`;
          break;
        // case 'checkMark':
        //   char = String.fromCodePoint(0x2714);
        //   break;
        // case 'down':
        //   char = String.fromCodePoint(0x25BC); // triangolo basso pieno - 0x25BD vuoto
        //   break;
        default:
          char = ''
      }
  
      return char.repeat(count);
    }
}